import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef } from 'react';
import type { EnergyByDate } from '../../../api/wattelzce-demo';
import { useI18n } from '../../../i18n';

interface EnergyTimeSeriesChartProps {
  data: EnergyByDate[];
  loading?: boolean;
  showForecast?: boolean;
}

function EnergyTimeSeriesChart({ data, loading, showForecast }: EnergyTimeSeriesChartProps) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { t } = useI18n();

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-96 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Debug log
  console.log('EnergyTimeSeriesChart data:', data.length, 'points');
  if (data.length > 0) {
    console.log('Date range:', data[0]?.date, 'to', data[data.length - 1]?.date);
  }

  // Check for empty data
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-96 flex items-center justify-center">
        <p className="text-gray-500">{t('noData')}</p>
      </div>
    );
  }

  // Separate actual and forecast data
  const actualData = data.filter(d => !d.isForecast);
  const forecastData = data.filter(d => d.isForecast);

  // Prepare series data
  const actualSeries = actualData.map(d => ({
    x: new Date(d.date).getTime(),
    y: d.energy,
  }));

  const forecastSeries = forecastData.map(d => ({
    x: new Date(d.date).getTime(),
    y: d.energy,
  }));

  // Generate upper/lower bounds for forecast visualization
  const forecastUpperSeries = forecastData.map(d => ({
    x: new Date(d.date).getTime(),
    y: d.energy * 1.15,
  }));

  const forecastLowerSeries = forecastData.map(d => ({
    x: new Date(d.date).getTime(),
    y: d.energy * 0.85,
  }));

  const series: Highcharts.SeriesOptionsType[] = [
    {
      type: 'areaspline',
      name: t('actualConsumption'),
      data: actualSeries,
      color: '#059669',
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, 'rgba(5, 150, 105, 0.3)'],
          [1, 'rgba(5, 150, 105, 0.05)'],
        ],
      },
      lineWidth: 2,
      marker: {
        enabled: false,
        symbol: 'circle',
        radius: 3,
      },
    },
  ];

  // Add forecast series if enabled
  if (showForecast && forecastSeries.length > 0) {
    // Connect actual to forecast
    const connectionPoint = actualSeries.length > 0 
      ? { ...actualSeries[actualSeries.length - 1] }
      : null;

    const forecastWithConnection = connectionPoint 
      ? [connectionPoint, ...forecastSeries]
      : forecastSeries;

    const upperWithConnection = connectionPoint
      ? [{ x: connectionPoint.x, y: connectionPoint.y * 1.15 }, ...forecastUpperSeries]
      : forecastUpperSeries;

    const lowerWithConnection = connectionPoint
      ? [{ x: connectionPoint.x, y: connectionPoint.y * 0.85 }, ...forecastLowerSeries]
      : forecastLowerSeries;

    // Upper bound (dashed, light)
    series.push({
      type: 'line',
      name: t('confidenceInterval'),
      data: upperWithConnection,
      color: 'rgba(59, 130, 246, 0.3)',
      dashStyle: 'Dot',
      lineWidth: 1,
      marker: { enabled: false },
      enableMouseTracking: false,
      showInLegend: false,
    });

    // Lower bound (dashed, light)
    series.push({
      type: 'line',
      name: t('confidenceInterval'),
      data: lowerWithConnection,
      color: 'rgba(59, 130, 246, 0.3)',
      dashStyle: 'Dot',
      lineWidth: 1,
      marker: { enabled: false },
      enableMouseTracking: false,
      showInLegend: false,
    });

    // Main forecast line
    series.push({
      type: 'line',
      name: t('forecast'),
      data: forecastWithConnection,
      color: '#3b82f6',
      dashStyle: 'Dash',
      lineWidth: 2,
      marker: {
        enabled: false,
      },
    });
  }

  const options: Highcharts.Options = {
    chart: {
      height: 350,
      style: {
        fontFamily: 'inherit',
      },
      zooming: {
        type: 'x',
      },
    },
    title: {
      text: t('energyOverTime'),
      style: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
      },
    },
    subtitle: {
      text: showForecast ? t('energyOverTimeForecast') : t('energyOverTimeSubtitle'),
      style: {
        fontSize: '12px',
        color: '#6b7280',
      },
    },
    xAxis: {
      type: 'datetime',
      labels: {
        style: {
          fontSize: '11px',
          color: '#6b7280',
        },
        format: '{value:%d %b}',
      },
      lineColor: '#e5e7eb',
      tickColor: '#e5e7eb',
      plotBands: showForecast && forecastData.length > 0 ? [{
        from: new Date(forecastData[0].date).getTime(),
        to: new Date(forecastData[forecastData.length - 1].date).getTime(),
        color: 'rgba(59, 130, 246, 0.05)',
        label: {
          text: t('forecast'),
          style: {
            color: '#3b82f6',
            fontSize: '10px',
          },
        },
      }] : undefined,
    },
    yAxis: {
      min: 0,
      title: {
        text: `${t('energy')} (${t('kwh')})`,
        style: {
          color: '#6b7280',
        },
      },
      labels: {
        style: {
          fontSize: '11px',
          color: '#6b7280',
        },
        format: '{value:,.0f}',
      },
      gridLineColor: '#f3f4f6',
    },
    tooltip: {
      shared: true,
      xDateFormat: '%d %B %Y',
      valueDecimals: 1,
      valueSuffix: ` ${t('kwh')}`,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      borderRadius: 8,
      shadow: true,
    },
    legend: {
      enabled: showForecast,
      align: 'center',
      verticalAlign: 'bottom',
      itemStyle: {
        fontSize: '12px',
        fontWeight: '500',
        color: '#374151',
      },
    },
    plotOptions: {
      series: {
        animation: {
          duration: 1000,
        },
      },
    },
    series,
    credits: {
      enabled: false,
    },
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />
    </div>
  );
}

export default EnergyTimeSeriesChart;
